import { GeoPoint, inverse, segmentDistance } from './model/geodesy';
import { PROFILE_SAMPLE_COUNT } from './model/ticks';
import type { MeasureMeasurement, MeasureSession } from './model/session';
import type { TrailNetwork } from './trailNetwork';

const toGeo = (c: number[]): GeoPoint => ({ lat: c[1], lng: c[0] });

/** The point on the hike or recording line nearest to [point]; the point itself when there is no line. */
export function snapToRoute(network: TrailNetwork | null, point: GeoPoint): GeoPoint {
  const snapped = network?.snap([point.lng, point.lat]);
  return snapped ? toGeo(snapped) : point;
}

/** The nearest point on the hike or recording line and how far along it that is (m); null without a line. */
export function locateOnRoute(network: TrailNetwork | null, point: GeoPoint): { point: GeoPoint; along: number } | null {
  const found = network?.locate([point.lng, point.lat]);
  return found ? { point: toGeo(found.coord), along: found.along } : null;
}

/**
 * The snapped measurement: the on-trail path between the snapped start and the target, measured
 * along the trail. The bearing stays the direct one between the two snapped ends, as on the apps.
 * Null when the two ends sit on trails that don't connect.
 */
export function measureAlongRoute(session: MeasureSession, network: TrailNetwork): MeasureMeasurement | null {
  if (!session.start || !session.target) return null;
  const path = network.path([session.start.lng, session.start.lat], [session.target.lng, session.target.lat]);
  if (!path || path.length < 2) return null;
  const points = path.map(toGeo);
  const cumulative = cumulativeDistances(points);
  const start = points[0];
  const target = points[points.length - 1];
  const hasElevations = path.every(hasHeight);
  const samples = sampleAlong(points, cumulative, PROFILE_SAMPLE_COUNT);
  return {
    start,
    target,
    routeMode: true,
    distance: cumulative[cumulative.length - 1],
    trueBearing: inverse(start, target).initialBearing,
    routePath: points,
    samplePoints: samples.map((s) => s.point),
    routeElevations: hasElevations ? samples.map((s) => lerp(path[s.index][2], path[s.index + 1]?.[2] ?? path[s.index][2], s.fraction)) : null,
  };
}

const hasHeight = (c: number[] | undefined) => !!c && typeof c[2] === 'number' && Number.isFinite(c[2]);

export function cumulativeDistances(points: GeoPoint[]): number[] {
  const out = [0];
  for (let i = 1; i < points.length; i++) out.push(out[i - 1] + segmentDistance(points[i - 1], points[i]));
  return out;
}

/** [count] points at equal distances along a polyline, ends included, with where each falls. */
export function sampleAlong(points: GeoPoint[], cumulative: number[], count: number) {
  const total = cumulative[cumulative.length - 1];
  const out: { point: GeoPoint; index: number; fraction: number }[] = [];
  let segment = 0;
  for (let i = 0; i < count; i++) {
    const at = count === 1 ? 0 : (total * i) / (count - 1);
    while (segment < points.length - 2 && cumulative[segment + 1] < at) segment += 1;
    const length = cumulative[segment + 1] - cumulative[segment];
    const fraction = length > 0 ? Math.min(1, Math.max(0, (at - cumulative[segment]) / length)) : 0;
    const a = points[segment];
    const b = points[segment + 1] ?? a;
    out.push({ point: { lat: lerp(a.lat, b.lat, fraction), lng: lerp(a.lng, b.lng, fraction) }, index: segment, fraction });
  }
  return out;
}

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
